import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  Image, Upload, X, Edit2, Trash2, Eye, EyeOff, 
  GripVertical, ChevronLeft, ChevronRight, Maximize2 
} from 'lucide-react';

const API_URL = "";

export const ArtGallery = ({ gallery = [], maxImages = 12, token, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingImage, setViewingImage] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set());
  const fileInputRef = useRef(null);

  // Filter gallery to only show images on persistent storage — legacy
  // Cloudinary URLs are dead now that the account is deactivated.
  const validGallery = gallery.filter(img => {
    const url = img.url || '';
    const isDeadCloudinary = url.includes('res.cloudinary.com') || url.includes('/image/upload/');
    const isPersistent = url.startsWith('/api/media/file/') || url.startsWith('http');
    return isPersistent && !isDeadCloudinary && !failedImages.has(img.id);
  });

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const handleImageError = (imageId) => {
    setFailedImages(prev => new Set([...prev, imageId]));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/members/gallery/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      toast.success('Artwork uploaded!');
      
      // Open edit modal for the new image
      setEditingImage(data.image);
      setEditForm({
        title: '',
        description: '',
        medium: '',
        year: '',
        is_public: true
      });
      
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.message || 'Failed to upload artwork');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleEditSubmit = async () => {
    if (!editingImage) return;

    try {
      const params = new URLSearchParams();
      if (editForm.title !== undefined) params.append('title', editForm.title);
      if (editForm.description !== undefined) params.append('description', editForm.description);
      if (editForm.medium !== undefined) params.append('medium', editForm.medium);
      if (editForm.year !== undefined) params.append('year', editForm.year);
      if (editForm.is_public !== undefined) params.append('is_public', editForm.is_public);

      const response = await fetch(
        `${API_URL}/api/members/gallery/${editingImage.id}?${params.toString()}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update');
      }

      toast.success('Artwork details updated');
      setEditingImage(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update artwork details');
    }
  };

  const handleDelete = async (imageId) => {
    setDeleting(imageId);
    try {
      const response = await fetch(`${API_URL}/api/members/gallery/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete');
      }

      toast.success('Artwork removed from gallery');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to remove artwork');
    } finally {
      setDeleting(null);
    }
  };

  const openEditModal = (image) => {
    setEditingImage(image);
    setEditForm({
      title: image.title || '',
      description: image.description || '',
      medium: image.medium || '',
      year: image.year || '',
      is_public: image.is_public !== false
    });
  };

  const navigateViewer = (direction) => {
    if (!viewingImage) return;
    const currentIndex = validGallery.findIndex(img => img.id === viewingImage.id);
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % validGallery.length;
    } else {
      newIndex = (currentIndex - 1 + validGallery.length) % validGallery.length;
    }
    setViewingImage(validGallery[newIndex]);
  };

  return (
    <div className="space-y-6" data-testid="art-gallery">
      {/* Upload Section */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-stone-900">Art Gallery</h3>
          <p className="text-sm text-stone-500">
            {validGallery.length} of {maxImages} artworks uploaded
            {gallery.length !== validGallery.length && (
              <span className="text-amber-600 ml-1">
                ({gallery.length - validGallery.length} pending sync)
              </span>
            )}
          </p>
        </div>
        
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleUpload}
            className="hidden"
            data-testid="gallery-upload-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || validGallery.length >= maxImages}
            className="bg-cafa-red hover:bg-cafa-red-900 text-white"
            data-testid="gallery-upload-btn"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Upload Artwork
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Gallery Grid - Only shows valid Cloudinary images */}
      {validGallery.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {validGallery.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map((image) => (
            <div 
              key={image.id} 
              className="group relative aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200"
              data-testid={`gallery-item-${image.id}`}
            >
              <img
                src={getImageUrl(image.url)}
                alt={image.title || 'Artwork'}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                onError={() => handleImageError(image.id)}
              />
              
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                {image.title && (
                  <p className="text-white font-medium text-sm text-center line-clamp-2">{image.title}</p>
                )}
                {image.medium && (
                  <p className="text-white/70 text-xs">{image.medium}</p>
                )}
                
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setViewingImage(image)}
                    className="bg-white/20 hover:bg-white/30 text-white"
                  >
                    <Maximize2 size={14} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openEditModal(image)}
                    className="bg-white/20 hover:bg-white/30 text-white"
                  >
                    <Edit2 size={14} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDelete(image.id)}
                    disabled={deleting === image.id}
                    className="bg-red-500/50 hover:bg-red-500/70 text-white"
                  >
                    {deleting === image.id ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </Button>
                </div>
              </div>

              {/* Visibility indicator */}
              {!image.is_public && (
                <div className="absolute top-2 right-2 bg-stone-900/70 rounded px-2 py-1">
                  <EyeOff size={12} className="text-white" />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-stone-50 rounded-lg border-2 border-dashed border-stone-200">
          <Image size={48} className="mx-auto mb-4 text-stone-300" />
          <h4 className="text-lg font-medium text-stone-700 mb-2">No artworks yet</h4>
          <p className="text-sm text-stone-500 mb-4">
            Upload images of your artwork to showcase in your gallery
          </p>
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            className="border-cafa-red text-cafa-red hover:bg-cafa-red-50"
          >
            <Upload size={16} className="mr-2" />
            Upload Your First Artwork
          </Button>
        </div>
      )}

      {/* Edit Modal */}
      {editingImage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Artwork Details</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingImage(null)}>
                <X size={18} />
              </Button>
            </div>

            {/* Preview */}
            <div className="mb-4">
              <img
                src={getImageUrl(editingImage.url)}
                alt="Preview"
                className="w-full h-40 object-cover rounded-lg"
              />
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  placeholder="Name of the artwork"
                  maxLength={100}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="medium">Medium</Label>
                  <Input
                    id="medium"
                    value={editForm.medium}
                    onChange={(e) => setEditForm({...editForm, medium: e.target.value})}
                    placeholder="e.g., Oil on Canvas"
                    maxLength={50}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="year">Year</Label>
                  <Input
                    id="year"
                    value={editForm.year}
                    onChange={(e) => setEditForm({...editForm, year: e.target.value})}
                    placeholder="e.g., 2024"
                    maxLength={4}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder="Brief description of the artwork..."
                  maxLength={500}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={editForm.is_public}
                  onChange={(e) => setEditForm({...editForm, is_public: e.target.checked})}
                  className="accent-cafa-red"
                />
                <Label htmlFor="is_public" className="cursor-pointer flex items-center gap-2">
                  {editForm.is_public ? <Eye size={16} /> : <EyeOff size={16} />}
                  Show in public gallery
                </Label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleEditSubmit}
                className="flex-1 bg-cafa-red hover:bg-cafa-red-900 text-white"
              >
                Save Details
              </Button>
              <Button variant="outline" onClick={() => setEditingImage(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen Viewer */}
      {viewingImage && (
        <div 
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50"
          onClick={() => setViewingImage(null)}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
            onClick={() => setViewingImage(null)}
          >
            <X size={32} />
          </button>

          {/* Navigation */}
          {validGallery.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
                onClick={(e) => { e.stopPropagation(); navigateViewer('prev'); }}
              >
                <ChevronLeft size={48} />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
                onClick={(e) => { e.stopPropagation(); navigateViewer('next'); }}
              >
                <ChevronRight size={48} />
              </button>
            </>
          )}

          {/* Image */}
          <div className="max-w-5xl max-h-[85vh] px-16" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(viewingImage.url)}
              alt={viewingImage.title || 'Artwork'}
              className="max-w-full max-h-[75vh] object-contain"
            />
            
            {/* Caption */}
            {(viewingImage.title || viewingImage.medium || viewingImage.year) && (
              <div className="text-center mt-4 text-white">
                {viewingImage.title && (
                  <h3 className="text-xl font-medium">{viewingImage.title}</h3>
                )}
                <p className="text-white/70 text-sm mt-1">
                  {[viewingImage.medium, viewingImage.year].filter(Boolean).join(' • ')}
                </p>
                {viewingImage.description && (
                  <p className="text-white/60 text-sm mt-2 max-w-2xl mx-auto">
                    {viewingImage.description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtGallery;
